/**
 * State of the dashboard canvas, kept free of React and network calls.
 *
 * A canvas is a fixed set of boxes. Each box holds one question and moves
 * through phases as that question is planned into a governed chart, saved,
 * and run. Every transition is a pure function here so the page and its hook
 * only decide *when* to move a box, never *how*.
 */
import { ApiError, type PanelDraft, type PanelPlan } from "../../services/api";
import type { PanelLayout, PanelResult } from "../../types";

export const BOX_COUNT = 4;
export const MAX_QUESTION_CHARS = 500;

/** What a box is asked to show; the user picks it per box. */
export const BOX_KINDS = ["headline", "trend", "comparison", "breakdown", "auto"] as const;
export type BoxKind = (typeof BOX_KINDS)[number];

/** A balanced dashboard: a headline figure, a trend, a comparison and a breakdown. */
const DEFAULT_KINDS: readonly BoxKind[] = ["headline", "trend", "comparison", "breakdown"];

/**
 * How each kind is said to the planner. The plan request carries only the
 * question, and the planner already follows a chart named in it.
 */
const KIND_HINT: Record<BoxKind, string> = {
  headline: "as a single headline number",
  trend: "as a line chart",
  comparison: "as a bar chart",
  breakdown: "as a table",
  auto: "",
};

const NAMES_A_CHART =
  /\b(charts?|graphs?|plots?|tables?|pie|donut|line|bars?|columns?|kpi|single number|headline number)\b/i;

/** Columns on the twelve-column dashboard grid that one box occupies. */
const BOX_WIDTH = 6;
const BOX_HEIGHT = 6;

export type BoxPhase =
  /** Editable. The only phase in which the question can be typed. */
  | "draft"
  /** Waiting for the agent to turn the question into a chart. */
  | "planning"
  /** A chart is saved and its governed query is executing. */
  | "running"
  | "ready"
  /** The agent needs the user to choose how to read the question. */
  | "clarify"
  /** The question needs several charts; the user keeps one. */
  | "multiple"
  /** Answered, but not as a chart this box can hold. */
  | "unsupported"
  | "failed";

/** Plan outcomes that stop for the user instead of producing a chart. */
export type PlanStop = "clarify" | "multiple" | "unsupported" | "failed";

export function isPlanStop(phase: string): phase is PlanStop {
  return (
    phase === "clarify" || phase === "multiple" || phase === "unsupported" || phase === "failed"
  );
}

export interface CanvasBox {
  /** Stable id; never changes while the canvas is open. */
  id: string;
  /** Position on the canvas, which is also its position on the dashboard. */
  slot: number;
  kind: BoxKind;
  question: string;
  phase: BoxPhase;
  /** What the user should read in this box, when it is not a chart. */
  message: string;
  options: string[];
  candidates: PanelDraft[];
  /** The chart chosen for this box, with the id it is saved under. */
  panel: PanelDraft | null;
  /** True once `panel` exists on the dashboard, so retrying only re-runs it. */
  saved: boolean;
  result: PanelResult | null;
  /** Whether trying again unchanged could succeed. */
  retryable: boolean;
}

export interface CanvasSource {
  contextId: string;
  contextVersion: string;
  name: string;
}

export interface FailureDetail {
  message: string;
  retryable: boolean;
}

export function createBox(index: number): CanvasBox {
  return {
    id: `box-${index + 1}`,
    slot: index,
    kind: DEFAULT_KINDS[index % DEFAULT_KINDS.length] ?? "auto",
    question: "",
    phase: "draft",
    message: "",
    options: [],
    candidates: [],
    panel: null,
    saved: false,
    result: null,
    retryable: false,
  };
}

export function createBoxes(count = BOX_COUNT): CanvasBox[] {
  return Array.from({ length: count }, (_, index) => createBox(index));
}

/** Why a question cannot be sent yet, or null when it can. */
export function questionProblem(question: string): string | null {
  const text = question.trim();
  if (!text) return "Type a question for this box.";
  if (text.length > MAX_QUESTION_CHARS) {
    return `Keep the question under ${MAX_QUESTION_CHARS} characters.`;
  }
  return null;
}

export function filledCount(boxes: CanvasBox[]): number {
  return boxes.filter((box) => questionProblem(box.question) === null).length;
}

/** The first submit runs every box together, so every box must be filled. */
export function canSubmit(boxes: CanvasBox[]): boolean {
  return (
    boxes.length > 0 &&
    boxes.every(
      (box) => box.phase === "draft" && questionProblem(box.question) === null,
    )
  );
}

export function isBusy(box: CanvasBox): boolean {
  return box.phase === "planning" || box.phase === "running";
}

export function readyCount(boxes: CanvasBox[]): number {
  return boxes.filter((box) => box.phase === "ready").length;
}

/** Where a box sits on the saved dashboard: two per row, in box order. */
export function slotLayout(index: number): PanelLayout {
  return {
    x: (index % 2) * BOX_WIDTH,
    y: Math.floor(index / 2) * BOX_HEIGHT,
    w: BOX_WIDTH,
    h: BOX_HEIGHT,
  };
}

/**
 * The planned chart as it will be saved for a given box.
 *
 * The id is chosen here rather than by the server so the run result can be
 * matched back to its box without relying on panel order.
 */
export function draftForSlot(
  draft: PanelDraft,
  slot: number,
  source: CanvasSource,
  panelId: string,
): PanelDraft {
  return {
    ...draft,
    id: panelId,
    layout: slotLayout(slot),
    contextId: draft.contextId || source.contextId,
    contextVersion: draft.contextVersion || source.contextVersion || null,
  };
}

/** A box's kind can change only while its question is being written. */
export function withKind(box: CanvasBox, kind: BoxKind): CanvasBox {
  return box.phase === "draft" ? { ...box, kind } : box;
}

/**
 * The question as sent to the planner: the box's kind added as a chart, unless
 * the user already named one or the addition would not fit.
 */
export function plannerQuestion(question: string, kind: BoxKind): string {
  const text = question.trim();
  const hint = KIND_HINT[kind];
  if (!hint || NAMES_A_CHART.test(text)) return text;
  const hinted = `${text} ${hint}`;
  return hinted.length <= MAX_QUESTION_CHARS ? hinted : text;
}

/** The question re-asked with the reading the user picked. */
export function clarifiedQuestion(question: string, option: string): string {
  return `${question.trim()} (${option.trim()})`.slice(0, MAX_QUESTION_CHARS);
}

function reset(box: CanvasBox): CanvasBox {
  return {
    ...box,
    message: "",
    options: [],
    candidates: [],
    result: null,
    retryable: false,
  };
}

/** Back to an editable question. Any chart the box held is forgotten. */
export function editing(box: CanvasBox): CanvasBox {
  return { ...reset(box), phase: "draft", panel: null, saved: false };
}

export function planning(box: CanvasBox, question = box.question): CanvasBox {
  return { ...reset(box), question, phase: "planning", panel: null, saved: false };
}

/** A chart has been chosen; saving and running it is the caller's next step. */
export function running(box: CanvasBox, panel: PanelDraft, saved = false): CanvasBox {
  return { ...reset(box), phase: "running", panel, saved };
}

export function markSaved(box: CanvasBox): CanvasBox {
  return { ...box, saved: true };
}

/**
 * Applies the agent's answer.
 *
 * Only `ok` yields a chart, returned in `running` with `panel` set but not yet
 * saved. The other statuses leave the box waiting on the user.
 */
export function applyPlan(box: CanvasBox, plan: PanelPlan): CanvasBox {
  const base = { ...reset(box), panel: null, saved: false };
  switch (plan.status) {
    case "ok": {
      const [panel] = plan.panels;
      if (!panel) {
        return failed(base, {
          message: "The agent returned no chart. Try again.",
          retryable: true,
        });
      }
      return running(base, panel);
    }
    case "multiple":
      return {
        ...base,
        phase: "multiple",
        message: plan.message || "This question needs more than one chart.",
        candidates: plan.panels,
      };
    case "clarify":
      return {
        ...base,
        phase: "clarify",
        message: plan.message || "Which did you mean?",
        options: plan.options,
      };
    case "unsupported":
    case "invalid":
      return {
        ...base,
        phase: "unsupported",
        message:
          plan.message || "This question cannot be shown as a chart. Try rephrasing it.",
      };
  }
}

export function applyResult(box: CanvasBox, result: PanelResult | undefined): CanvasBox {
  if (!result) {
    return failed(box, {
      message: "The chart was saved but did not return data. Retry to run it again.",
      retryable: true,
    });
  }
  if (result.status === "error") {
    return failed(box, {
      message: result.error?.message || "The governed query failed.",
      retryable: result.error?.retryable ?? true,
    });
  }
  return { ...reset(box), phase: "ready", result };
}

/** Keeps `panel` and `saved`, so retrying resumes from the step that failed. */
export function failed(box: CanvasBox, detail: FailureDetail): CanvasBox {
  return {
    ...reset(box),
    phase: "failed",
    message: detail.message,
    retryable: detail.retryable,
  };
}

/** Wording for an error the user can act on, without leaking internals. */
export function describeError(error: unknown): FailureDetail {
  if (error instanceof ApiError) {
    if (error.status === 0) {
      return {
        message: "The analytics service could not be reached. Check your connection and retry.",
        retryable: true,
      };
    }
    if (error.status === 429) {
      return {
        message: "Too many questions at once. Wait a moment, then retry.",
        retryable: true,
      };
    }
    if (error.status >= 500) {
      return {
        message: error.message || "The analytics service is unavailable. Retry in a moment.",
        retryable: true,
      };
    }
    return { message: error.message, retryable: error.retryable };
  }
  return { message: "Something went wrong. Retry in a moment.", retryable: true };
}
